import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const Hierarchy = ({ primary }) => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [expandedSubs, setExpandedSubs] = useState({});
  const [showAddSub, setShowAddSub] = useState(false);
  const [showAddLco, setShowAddLco] = useState(null); // sub_id
  const [editUser, setEditUser] = useState(null);
  const [subForm, setSubForm] = useState({ name: '', email: '', password: '', phone: '', area: '', code: '' });
  const [lcoForm, setLcoForm] = useState({ name: '', email: '', password: '', phone: '', area: '', lco_id: '' });

  const showMsg = (text, type = 'success') => { setMsg({ text, type }); setTimeout(() => setMsg({ text: '', type: '' }), 4000); };

  const inp = { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', boxSizing: 'border-box', outline: 'none' };
  const lbl = { fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '5px' };

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [usersRes, devicesRes] = await Promise.all([
        axios.get('/api/users'),
        axios.get('/api/devices')
      ]);
      setUsers(usersRes.data.users || []);
      setDevices(devicesRes.data.devices || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const subs = users.filter(u => u.role === 'sub_dist_admin');
  const lcos = users.filter(u => u.role === 'lco_admin');

  const getLcosForSub = (subId) => lcos.filter(l => l.sub_dist_id && l.sub_dist_id.toString() === subId.toString());
  const getDevicesForLco = (lcoId) => devices.filter(d => d.lco_id && d.lco_id.toString() === lcoId.toString());
  const getDevicesForSub = (subId) => devices.filter(d => d.sub_dist_id && d.sub_dist_id.toString() === subId.toString());
  const unassignedDevices = devices.filter(d => !d.sub_dist_id && !d.lco_id);

  const handleAddSub = async () => {
    if (!subForm.name || !subForm.email || !subForm.password) { showMsg('Name, email and password required', 'error'); return; }
    try {
      await axios.post('/api/users', { ...subForm, role: 'sub_dist_admin', lco_id: subForm.code });
      showMsg('Sub-Distribution created!');
      setShowAddSub(false);
      setSubForm({ name: '', email: '', password: '', phone: '', area: '', code: '' });
      loadData();
    } catch (e) { showMsg(e.response?.data?.message || 'Failed', 'error'); }
  };

  const handleAddLco = async (subId) => {
    if (!lcoForm.name || !lcoForm.lco_id || !lcoForm.password) { showMsg('Name, LCO ID and password required', 'error'); return; }
    try {
      await axios.post('/api/users', { ...lcoForm, role: 'lco_admin', email: lcoForm.lco_id + '@fibraacs.local', sub_dist_id: subId });
      showMsg('LCO created!');
      setShowAddLco(null);
      setLcoForm({ name: '', email: '', password: '', phone: '', area: '', lco_id: '' });
      loadData();
    } catch (e) { showMsg(e.response?.data?.message || 'Failed', 'error'); }
  };

  const handleUpdate = async () => {
    try {
      await axios.put('/api/users/' + editUser._id, editUser);
      showMsg('Updated!');
      setEditUser(null);
      loadData();
    } catch (e) { showMsg(e.response?.data?.message || 'Failed', 'error'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm('Delete ' + name + '? This will not remove their devices.')) return;
    try {
      await axios.delete('/api/users/' + id);
      showMsg('Deleted!');
      loadData();
    } catch (e) { showMsg(e.response?.data?.message || 'Failed', 'error'); }
  };

  const canManage = ['superadmin', 'tenant_admin', 'dist_admin'].includes(user?.role);

  if (loading) return <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>Loading hierarchy...</div>;

  return (
    <div>
      <style>{`@keyframes slideIn { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }`}</style>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: '0 0 4px', fontSize: '24px', fontWeight: '800', color: '#0f172a' }}>🌐 Network Hierarchy</h1>
          <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>{subs.length} Sub-Distributions · {lcos.length} LCOs · {devices.length} Devices</p>
        </div>
        {canManage && (
          <button onClick={() => setShowAddSub(!showAddSub)}
            style={{ padding: '10px 20px', background: showAddSub ? '#ef4444' : primary, color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '700' }}>
            {showAddSub ? '✕ Cancel' : '+ Add Sub-Distribution'}
          </button>
        )}
      </div>

      {/* Message */}
      {msg.text && (
        <div style={{ background: msg.type === 'error' ? '#fef2f2' : '#ecfdf5', border: `1px solid ${msg.type === 'error' ? '#fecaca' : '#bbf7d0'}`, color: msg.type === 'error' ? '#dc2626' : '#16a34a', padding: '12px 16px', borderRadius: '10px', marginBottom: '16px', fontSize: '14px', fontWeight: '500', animation: 'slideIn 0.2s ease' }}>
          {msg.type === 'error' ? '⚠️' : '✅'} {msg.text}
        </div>
      )}

      {/* Add Sub Form */}
      {showAddSub && (
        <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', marginBottom: '24px', animation: 'slideIn 0.2s ease' }}>
          <h3 style={{ margin: '0 0 20px', fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>🏘️ New Sub-Distribution</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' }}>
            <div><label style={lbl}>Name *</label><input style={inp} value={subForm.name} onChange={e => setSubForm({ ...subForm, name: e.target.value })} placeholder="e.g. PCI Networks" /></div>
            <div><label style={lbl}>Sub Code (Login ID) *</label><input style={inp} value={subForm.code} onChange={e => setSubForm({ ...subForm, code: e.target.value, email: e.target.value.toLowerCase() + '@fibraacs.local' })} placeholder="e.g. KB13B" /></div>
            <div><label style={lbl}>Password *</label><input style={inp} type="password" value={subForm.password} onChange={e => setSubForm({ ...subForm, password: e.target.value })} placeholder="Min 6 chars" /></div>
            <div><label style={lbl}>Contact Phone</label><input style={inp} value={subForm.phone} onChange={e => setSubForm({ ...subForm, phone: e.target.value })} placeholder="Phone number" /></div>
            <div><label style={lbl}>Area</label><input style={inp} value={subForm.area} onChange={e => setSubForm({ ...subForm, area: e.target.value })} placeholder="e.g. Payyanur" /></div>
          </div>
          <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
            <button onClick={handleAddSub} style={{ padding: '11px 24px', background: primary, color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '14px' }}>💾 Create Sub-Distribution</button>
            <button onClick={() => setShowAddSub(false)} style={{ padding: '11px 20px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Hierarchy Tree */}
      {subs.length === 0 ? (
        <div style={{ background: 'white', borderRadius: '16px', padding: '60px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>🏘️</div>
          <div style={{ fontSize: '16px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>No Sub-Distributions yet</div>
          <div style={{ fontSize: '14px', color: '#94a3b8' }}>Click "+ Add Sub-Distribution" to get started</div>
        </div>
      ) : (
        subs.map(sub => {
          const subLcos = getLcosForSub(sub._id);
          const subDevices = getDevicesForSub(sub._id);
          const isExpanded = expandedSubs[sub._id] !== false;
          const onlineDevices = subDevices.filter(d => d.status === 'online').length;

          return (
            <div key={sub._id} style={{ background: 'white', borderRadius: '16px', marginBottom: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
              {/* Sub Header */}
              <div style={{ padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '14px', cursor: 'pointer', borderBottom: isExpanded ? '1px solid #f1f5f9' : 'none' }}
                onClick={() => setExpandedSubs({ ...expandedSubs, [sub._id]: !isExpanded })}>
                <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0 }}>🏘️</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '3px' }}>
                    <span style={{ fontWeight: '700', fontSize: '16px', color: '#0f172a' }}>{sub.name}</span>
                    <span style={{ background: '#ecfdf5', color: '#16a34a', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '700' }}>SUB</span>
                    {sub.lco_id && <span style={{ background: '#f1f5f9', color: '#475569', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontFamily: 'monospace' }}>{sub.lco_id}</span>}
                  </div>
                  <div style={{ fontSize: '12px', color: '#94a3b8' }}>{sub.area || 'No area'} · {sub.phone || 'No phone'}</div>
                </div>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: '700', fontSize: '18px', color: '#0f172a' }}>{subLcos.length}</div>
                    <div style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase' }}>LCOs</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: '700', fontSize: '18px', color: '#0f172a' }}>{subDevices.length}</div>
                    <div style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase' }}>Devices</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: '700', fontSize: '18px', color: '#16a34a' }}>{onlineDevices}</div>
                    <div style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase' }}>Online</div>
                  </div>
                  {canManage && (
                    <div style={{ display: 'flex', gap: '6px' }} onClick={e => e.stopPropagation()}>
                      <button onClick={() => setEditUser({ ...sub })} style={{ padding: '5px 10px', background: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>✏️</button>
                      <button onClick={() => handleDelete(sub._id, sub.name)} style={{ padding: '5px 10px', background: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>🗑️</button>
                    </div>
                  )}
                  <span style={{ color: '#94a3b8', fontSize: '18px' }}>{isExpanded ? '▲' : '▼'}</span>
                </div>
              </div>

              {/* LCOs under this Sub */}
              {isExpanded && (
                <div style={{ padding: '16px 20px' }}>
                  {subLcos.map(lco => {
                    const lcoDevices = getDevicesForLco(lco._id);
                    const lcoOnline = lcoDevices.filter(d => d.status === 'online').length;
                    return (
                      <div key={lco._id} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px', background: '#f8fafc', borderRadius: '12px', marginBottom: '10px', border: '1px solid #f1f5f9' }}>
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#d97706', flexShrink: 0, marginLeft: '8px' }} />
                        <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 }}>👤</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                            <span style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b' }}>{lco.name}</span>
                            <span style={{ background: '#fffbeb', color: '#d97706', padding: '1px 7px', borderRadius: '20px', fontSize: '11px', fontWeight: '700' }}>LCO</span>
                            {lco.lco_id && <span style={{ background: '#f1f5f9', color: '#475569', padding: '1px 7px', borderRadius: '20px', fontSize: '11px', fontFamily: 'monospace' }}>{lco.lco_id}</span>}
                          </div>
                          <div style={{ fontSize: '11px', color: '#94a3b8' }}>{lco.area || 'No area'} · {lco.phone || 'No phone'}</div>
                        </div>
                        <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontWeight: '700', fontSize: '16px', color: '#0f172a' }}>{lcoDevices.length}</div>
                            <div style={{ fontSize: '10px', color: '#94a3b8' }}>Devices</div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontWeight: '700', fontSize: '16px', color: '#16a34a' }}>{lcoOnline}</div>
                            <div style={{ fontSize: '10px', color: '#94a3b8' }}>Online</div>
                          </div>
                          {canManage && (
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button onClick={() => setEditUser({ ...lco })} style={{ padding: '5px 10px', background: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>✏️</button>
                              <button onClick={() => handleDelete(lco._id, lco.name)} style={{ padding: '5px 10px', background: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>🗑️</button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Add LCO Button */}
                  {canManage && (
                    <div>
                      {showAddLco === sub._id ? (
                        <div style={{ background: '#fffbeb', borderRadius: '12px', padding: '18px', border: '1px solid #fde68a', animation: 'slideIn 0.2s ease' }}>
                          <div style={{ fontWeight: '700', fontSize: '13px', color: '#92400e', marginBottom: '14px' }}>➕ Add LCO under {sub.name}</div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                            <div><label style={lbl}>LCO Name *</label><input style={inp} value={lcoForm.name} onChange={e => setLcoForm({ ...lcoForm, name: e.target.value })} placeholder="e.g. Anjali Comms" /></div>
                            <div><label style={lbl}>LCO ID (Login) *</label><input style={inp} value={lcoForm.lco_id} onChange={e => setLcoForm({ ...lcoForm, lco_id: e.target.value })} placeholder="e.g. KB13B05" /></div>
                            <div><label style={lbl}>Password *</label><input style={inp} type="password" value={lcoForm.password} onChange={e => setLcoForm({ ...lcoForm, password: e.target.value })} placeholder="Min 6 chars" /></div>
                            <div><label style={lbl}>Phone</label><input style={inp} value={lcoForm.phone} onChange={e => setLcoForm({ ...lcoForm, phone: e.target.value })} placeholder="Phone" /></div>
                            <div><label style={lbl}>Area</label><input style={inp} value={lcoForm.area} onChange={e => setLcoForm({ ...lcoForm, area: e.target.value })} placeholder="Area" /></div>
                          </div>
                          <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
                            <button onClick={() => handleAddLco(sub._id)} style={{ padding: '10px 20px', background: primary, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '13px' }}>💾 Create LCO</button>
                            <button onClick={() => setShowAddLco(null)} style={{ padding: '10px 16px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <button onClick={() => { setShowAddLco(sub._id); setLcoForm({ name: '', email: '', password: '', phone: '', area: '', lco_id: '' }); }}
                          style={{ width: '100%', padding: '12px', background: 'transparent', border: `2px dashed ${primary}40`, borderRadius: '10px', cursor: 'pointer', color: primary, fontWeight: '600', fontSize: '13px' }}>
                          + Add LCO under {sub.name}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })
      )}

      {/* Unassigned Devices */}
      {unassignedDevices.length > 0 && (
        <div style={{ background: '#fffbeb', borderRadius: '16px', padding: '18px 20px', border: '1px solid #fde68a', marginTop: '16px' }}>
          <div style={{ fontWeight: '700', color: '#92400e', marginBottom: '4px' }}>⚠️ {unassignedDevices.length} Unassigned Device{unassignedDevices.length > 1 ? 's' : ''}</div>
          <div style={{ fontSize: '13px', color: '#b45309' }}>These devices are not assigned to any Sub-Distribution or LCO. Assign them from the Devices page.</div>
        </div>
      )}

      {/* Edit Modal */}
      {editUser && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, backdropFilter: 'blur(4px)' }}>
          <div style={{ background: 'white', borderRadius: '20px', padding: '32px', width: '480px', boxShadow: '0 24px 60px rgba(0,0,0,0.3)', animation: 'slideIn 0.2s ease' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>✏️ Edit {editUser.role === 'lco_admin' ? 'LCO' : 'Sub-Distribution'}</h3>
              <button onClick={() => setEditUser(null)} style={{ background: '#f1f5f9', border: 'none', borderRadius: '8px', width: '32px', height: '32px', cursor: 'pointer', fontSize: '16px' }}>✕</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={{ gridColumn: '1/-1' }}><label style={lbl}>Name</label><input style={inp} value={editUser.name || ''} onChange={e => setEditUser({ ...editUser, name: e.target.value })} /></div>
              <div><label style={lbl}>Phone</label><input style={inp} value={editUser.phone || ''} onChange={e => setEditUser({ ...editUser, phone: e.target.value })} /></div>
              <div><label style={lbl}>Area</label><input style={inp} value={editUser.area || ''} onChange={e => setEditUser({ ...editUser, area: e.target.value })} /></div>
              <div><label style={lbl}>Email</label><input style={inp} value={editUser.email || ''} onChange={e => setEditUser({ ...editUser, email: e.target.value })} /></div>
              <div><label style={lbl}>New Password (optional)</label><input style={inp} type="password" onChange={e => setEditUser({ ...editUser, password: e.target.value })} placeholder="Leave blank to keep" /></div>
              <div style={{ gridColumn: '1/-1' }}><label style={lbl}>Status</label>
                <select style={inp} value={editUser.is_active ? 'true' : 'false'} onChange={e => setEditUser({ ...editUser, is_active: e.target.value === 'true' })}>
                  <option value="true">🟢 Active</option>
                  <option value="false">🔴 Inactive</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button onClick={handleUpdate} style={{ flex: 1, padding: '12px', background: primary, color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '14px' }}>💾 Save Changes</button>
              <button onClick={() => setEditUser(null)} style={{ padding: '12px 20px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Hierarchy;
